import AddAutomaticallyButton from './AddAutomaticallyButton';
import AddNewTableEntryButton from './AddNewTableEntryButton';
import TrackerTableDownloadButton from './TrackerTableDownloadButton';
import { Button } from '@mui/material';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  getTrackerViewColor,
  TrackerDataType,
  trackerHeaderBlueByType,
  trackerHeaderBlueHoverByType,
} from '../../shared/constants/constants';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import BulkEditQueryAdder from '../QueryAdder/BulkEditQueryAdder';
import EditPencilIcon from '../TrackerFilter/Icons/EditPencilIcon';
import {
  trackerToolbarButtonTypographySx,
  trackerToolbarNeutralHoverFill,
  trackerToolbarNeutralOutlineColor,
  trackerToolbarNeutralTextColor,
} from '../TrackerFilter/trackerFilterToolbarStyles';
import { logPalette } from '../../theme/palette';

const trackerEventHandlerPropType = PropTypes.shape({
  type: PropTypes.oneOf([
    TrackerDataType.MEDIA_INTERACTION,
    TrackerDataType.SERVICE,
    TrackerDataType.ENGAGEMENT,
    TrackerDataType.CONTENT_LOG,
  ]).isRequired,
  flattenForExport: PropTypes.func.isRequired,
  newItemModalTitle: PropTypes.string,
});

export const TrackerTableActionButtons = (props) => {
  const {
    eventHandler,
    dropdownTable = {},
    currMonth,
    rows,
    isBulkEdit,
    selectedBulkEditRowIds,
    selectedBulkEditRowsCount,
    onSwitchToBulkEditView,
    onSwitchToMonthlyView,
    hasAutoAdd = true,
    detailedExportContext,
  } = props;

  const [openBulkEditPopup, setOpenBulkEditPopup] = useState(false);
  const componentColor = getTrackerViewColor(eventHandler.type);
  const headerBlue = trackerHeaderBlueByType[eventHandler.type] ?? trackerHeaderBlueByType[TrackerDataType.SERVICE];
  const headerBlueHover =
    trackerHeaderBlueHoverByType[eventHandler.type] ?? trackerHeaderBlueHoverByType[TrackerDataType.SERVICE];
  const buttonOutlineColor = trackerToolbarNeutralOutlineColor;
  const buttonLabelColor = trackerToolbarNeutralTextColor;
  const buttonSoftFill = 'transparent';
  const buttonSoftFillHover = trackerToolbarNeutralHoverFill;
  const serviceBlueIconColor = logPalette.service.main;
  const toolbarButtonSx = {
    height: '32px',
    ...trackerToolbarButtonTypographySx,
  };
  const outlinedBlueButtonSx = {
    ...toolbarButtonSx,
    color: buttonLabelColor,
    borderColor: buttonOutlineColor,
    backgroundColor: buttonSoftFill,
    '& .MuiButton-startIcon .MuiSvgIcon-root': {
      fontSize: 18,
      color: serviceBlueIconColor,
    },
    '&:hover': {
      borderColor: buttonOutlineColor,
      backgroundColor: buttonSoftFillHover,
    },
  };

  const getRowType = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return selectedBulkEditRowsCount > 1 ? 'Interactions' : 'Interaction';
      case TrackerDataType.SERVICE:
        return selectedBulkEditRowsCount > 1 ? 'Services' : 'Service';
      case TrackerDataType.ENGAGEMENT:
        return selectedBulkEditRowsCount > 1 ? 'Engagements' : 'Engagement';
      case TrackerDataType.CONTENT_LOG:
        return selectedBulkEditRowsCount > 1 ? 'Content Logs' : 'Content Log';
      default:
        return selectedBulkEditRowsCount > 1 ? 'Items' : 'Item';
    }
  };

  if (!isBulkEdit) {
    return (
      <>
        <AddNewTableEntryButton dropdownTable={dropdownTable} eventHandler={eventHandler} currMonth={currMonth} />
        {hasAutoAdd && <AddAutomaticallyButton eventHandler={eventHandler} />}
        <Button
          variant="outlined"
          color={componentColor}
          sx={outlinedBlueButtonSx}
          onClick={() => onSwitchToBulkEditView()}
          data-cy="openBulkEditViewButton"
          startIcon={<EditPencilIcon />}
        >
          Edit Multiple
        </Button>
        <TrackerTableDownloadButton
          rows={rows}
          dropdowns={dropdownTable}
          flattenerCallback={eventHandler.flattenForExport}
          filename={eventHandler.type}
          color={componentColor}
          variant="outlined"
          buttonSx={outlinedBlueButtonSx}
          detailedExportContext={detailedExportContext}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color={componentColor}
        sx={{
          ...toolbarButtonSx,
          backgroundColor: headerBlue,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: headerBlueHover,
          },
        }}
        disabled={selectedBulkEditRowsCount === 0}
        onClick={() => setOpenBulkEditPopup(true)}
        data-cy="openBulkEditPopupButton"
      >
        Edit {selectedBulkEditRowsCount} {getRowType()}
      </Button>
      <Button
        variant="outlined"
        color={componentColor}
        sx={outlinedBlueButtonSx}
        onClick={() => onSwitchToMonthlyView()}
        data-cy="cancelBulkEditViewButton"
      >
        Cancel
      </Button>
      <QueryAdderDrawer openForm={openBulkEditPopup} setOpenForm={setOpenBulkEditPopup} useLegacyPadding>
        <BulkEditQueryAdder
          selectedBulkEditRowIds={selectedBulkEditRowIds}
          eventHandler={eventHandler}
          dropdownTable={dropdownTable}
          currMonth={currMonth}
          rowType={getRowType()}
          onClose={() => setOpenBulkEditPopup(false)}
        />
      </QueryAdderDrawer>
    </>
  );
};

TrackerTableActionButtons.propTypes = {
  eventHandler: trackerEventHandlerPropType.isRequired,
  dropdownTable: PropTypes.object,
  currMonth: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cells: PropTypes.object,
    })
  ).isRequired,
  isBulkEdit: PropTypes.bool.isRequired,
  selectedBulkEditRowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  selectedBulkEditRowsCount: PropTypes.number.isRequired,
  onSwitchToBulkEditView: PropTypes.func.isRequired,
  onSwitchToMonthlyView: PropTypes.func.isRequired,
  hasAutoAdd: PropTypes.bool,
  detailedExportContext: PropTypes.object,
};
